/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @next/next/no-img-element */
"use client"

import type React from "react"
import { forwardRef, useMemo, useState } from "react"
import { Input, Select, DatePicker, Space, Form } from "antd"
import { EyeInvisibleOutlined, EyeTwoTone, SearchOutlined, CloseOutlined } from "@ant-design/icons"
import dayjs, { type Dayjs } from "dayjs"
import { AiOutlineInfoCircle } from "react-icons/ai"
import { getFlagImageUrl, countryCodes as allCountryCodes } from "@/lib/utils/misc/countryCodes"
import OptionalView from "@/components/misc/OptionalView"

interface SelectOption {
  value: string
  label: string
  code?: string
}

interface CountryCode {
  code: string
  flag: string
  country: string
}

interface Country {
  code: string
  name: string
  flag: string
}

interface PhoneNumberCountry {
  code: string
  name: string
  dialCode: string
  flag: string
}

interface CustomInputProps {
  label?: string
  max?: number
  min?: number
  error?: string
  showPasswordToggle?: boolean
  onPasswordToggle?: () => void
  showPassword?: boolean
  className?: string
  placeholder?: string
  type?: string
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement>) => void
  maxLength?: number
  isSelect?: boolean
  options?: SelectOption[]
  onSelectChange?: (value: string) => void
  selectValue?: string
  isMultiSelect?: boolean
  multiSelectValue?: string[]
  onMultiSelectChange?: (values: string[]) => void
  multiSelectOptions?: SelectOption[]
  // End multiselect props
  isPhoneInput?: boolean
  phoneValue?: string
  onPhoneChange?: (value: string) => void
  selectedCountryCode?: string
  onCountryCodeChange?: (code: string) => void
  countryCodes?: CountryCode[]
  isDatePicker?: boolean
  dateValue?: string | Dayjs
  onDateChange?: (date: Dayjs | null, dateString: string | string[]) => void
  disabledDateFunction?: (current: Dayjs) => boolean
  isCountrySearch?: boolean
  countries?: Country[]
  selectedCountry?: string
  onCountrySelect?: (country: string) => void
  isPhoneNumberInput?: boolean
  phoneNumberValue?: string
  onPhoneNumberChange?: (value: string) => void
  selectedPhoneCode?: string
  onPhoneCodeSelect?: (phoneCode: string, countryKey?: string) => void
  selectedCountryKey?: string
  phoneCountries?: Country[]
  isAddressInput?: boolean
  addressNumber?: string
  addressText?: string
  onAddressNumberChange?: (value: string) => void
  onAddressTextChange?: (value: string) => void
  Prefix?: any
  Suffix?: any
  labelClassName?: string
  inputClass?: string
  inputStyle?: React.CSSProperties
  outerClass?: string
  required?: boolean
  nonEditable?: boolean
  nonEditableText?: string
  disabled?: boolean
  isCheckbox?: boolean
  checkboxLabel?: string
  checkboxValue?: boolean
  onCheckboxChange?: (checked: boolean) => void
  checkboxDescription?: string
  isTextArea?: boolean
  textAreaRows?: number
  textAreaValue?: string
  onTextAreaChange?: (value: string) => void
  dropdownAsFormItem?: boolean
  fieldName?: string
  fieldRules?: any[]
  disableInputHelp?: boolean
  useRedAsterisk?: boolean
}

const defaultCountryCodes: CountryCode[] = [
  { code: "+234", flag: "🇳🇬", country: "Nigeria" },
  { code: "+1", flag: "🇺🇸", country: "United States" },
  { code: "+44", flag: "🇬🇧", country: "United Kingdom" },
  { code: "+91", flag: "🇮🇳", country: "India" },
  { code: "+86", flag: "🇨🇳", country: "China" },
]

const defaultCountries: Country[] = [
  { code: "AE", name: "United Arab Emirates", flag: "https://flagcdn.com/w40/ae.png" },
  { code: "GB", name: "United Kingdom", flag: "https://flagcdn.com/w40/gb.png" },
  { code: "TZ", name: "United Republic of Tanzania", flag: "https://flagcdn.com/w40/tz.png" },
  { code: "US", name: "United States", flag: "https://flagcdn.com/w40/us.png" },
  { code: "NG", name: "Nigeria", flag: "https://flagcdn.com/w40/ng.png" },
  { code: "CA", name: "Canada", flag: "https://flagcdn.com/w40/ca.png" },
  { code: "AU", name: "Australia", flag: "https://flagcdn.com/w40/au.png" },
  { code: "DE", name: "Germany", flag: "https://flagcdn.com/w40/de.png" },
  { code: "FR", name: "France", flag: "https://flagcdn.com/w40/fr.png" },
  { code: "IN", name: "India", flag: "https://flagcdn.com/w40/in.png" },
]

const createCountryKey = (countryCode: string, dialCode: string) => `${countryCode}-${dialCode}`

const CustomInput = forwardRef<any, CustomInputProps>(
  (
    {
      label,
      error,
      showPasswordToggle,
      onPasswordToggle,
      showPassword,
      className,
      type,
      onChange,
      value,
      onBlur,
      inputClass,
      onPaste,
      maxLength,
      isSelect,
      options,
      onSelectChange,
      selectValue,
      isMultiSelect,
      multiSelectValue,
      onMultiSelectChange,
      max,
      min,
      multiSelectOptions,
      // End multiselect destructuring
      isPhoneInput,
      phoneValue,
      onPhoneChange,
      selectedCountryCode,
      onCountryCodeChange,
      countryCodes = defaultCountryCodes,
      isDatePicker,
      dateValue,
      onDateChange,
      disabledDateFunction,
      isCountrySearch,
      countries = defaultCountries,
      selectedCountry,
      onCountrySelect,
      isPhoneNumberInput,
      phoneNumberValue,
      onPhoneNumberChange,
      selectedPhoneCode,
      onPhoneCodeSelect,
      selectedCountryKey,
      phoneCountries = defaultCountries,
      isAddressInput,
      addressNumber,
      addressText,
      onAddressNumberChange,
      onAddressTextChange,
      Prefix,
      Suffix,
      labelClassName,
      disabled,
      inputStyle = {},
      outerClass = "",
      required,
      nonEditable,
      nonEditableText,
      isCheckbox,
      checkboxLabel,
      checkboxValue,
      onCheckboxChange,
      checkboxDescription,
      isTextArea,
      textAreaRows = 4,
      textAreaValue,
      onTextAreaChange,
      dropdownAsFormItem,
      fieldName,
      fieldRules,
      disableInputHelp,
      useRedAsterisk,
      ...props
    },
    ref,
  ) => {
    const [searchTerm, setSearchTerm] = useState("")
    const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false)
    //  const [isPhoneCountryDropdownOpen, setIsPhoneCountryDropdownOpen] = useState(false)
    const [phoneCountrySearchTerm] = useState("")

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(e)
      }
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if (onBlur) {
        onBlur(e)
      }
    }

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (onPaste) {
        onPaste(e)
      }
    }

    const handleSelectChange = (value: string) => {
      if (onSelectChange) {
        onSelectChange(value)
      }
    }

    const handleMultiSelectChange = (values: string[]) => {
      if (onMultiSelectChange) {
        onMultiSelectChange(values)
      }
    }

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onPhoneChange) {
        onPhoneChange(e.target.value)
      }
    }

    const handleCountryCodeChange = (code: string) => {
      if (onCountryCodeChange) {
        onCountryCodeChange(code)
      }
    }

    const handleDateChange = (date: Dayjs | null, dateString: string | string[]) => {
      if (onDateChange) {
        onDateChange(date, dateString)
      }
    }

    const handleCountrySearch = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value)
    }

    const handleCountrySelect = (countryName: string) => {
      if (onCountrySelect) {
        onCountrySelect(countryName)
      }
      setIsCountryDropdownOpen(false)
      setSearchTerm("")
    }

    const clearCountrySearch = () => {
      setSearchTerm("")
    }

    const handlePhoneNumberInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (onPhoneNumberChange) {
        onPhoneNumberChange(e.target.value)
      }
    }

    const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (onTextAreaChange) {
        onTextAreaChange(e.target.value)
      }
    }

    const filteredCountries = countries.filter((country) => {
      const displayName = (country as any).nationality || (country as any).name || ""
      return displayName.toLowerCase().includes(searchTerm.toLowerCase())
    })

    // const filteredPhoneCountries = phoneCountries.filter((country) =>
    //   country.name.toLowerCase().includes(phoneCountrySearchTerm.toLowerCase()),
    // )

    const disabledDate = (current: Dayjs) => {
      if (disabledDateFunction) {
        return disabledDateFunction(current)
      }
      // Default behavior: disable dates after 18 years ago (for DOB)
      const eighteenYearsAgo = dayjs().subtract(18, "year")
      return current && current.isAfter(eighteenYearsAgo, "day")
    }

    // Memoize so we don't create a new Dayjs instance every render (avoids DatePicker update loop)
    const datePickerValue = useMemo(() => {
      if (dateValue == null || dateValue === "") return null
      return dayjs(dateValue as string)
    }, [
      dateValue == null || dateValue === ""
        ? null
        : typeof dateValue === "string"
          ? dateValue
          : String((dateValue as Dayjs).valueOf()),
    ])

    return (
      <div className={`mb-6 ${outerClass}`}>
        {label && (
          <label className={`block font-normal text-sm mb-2 text-${labelClassName || "gray-900"}`}>
            {label}
            {useRedAsterisk && <span className="text-red-500 text-lg">&nbsp;*</span>}
          </label>
        )}
        <div className={`relative ${inputClass}`}>
          <OptionalView condition={Prefix !== undefined}>{Prefix}</OptionalView>
          <OptionalView condition={Suffix !== undefined}>{Suffix}</OptionalView>

          {isCheckbox ? (
            <div className="flex items-start space-x-3">
              <input
                ref={ref}
                type="checkbox"
                checked={checkboxValue || false}
                onChange={(e) => onCheckboxChange?.(e.target.checked)}
                disabled={disabled}
                className="w-5 h-5 mt-1 rounded border-gray-300 text-blue-600 cursor-pointer accent-blue-600"
              />
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-900 cursor-pointer block">{checkboxLabel}</label>
                {checkboxDescription && <p className="text-xs text-gray-500 mt-1">{checkboxDescription}</p>}
              </div>
            </div>
          ) : isTextArea ? (
            <Input.TextArea
              ref={ref}
              value={textAreaValue}
              onChange={handleTextAreaChange}
              placeholder={props.placeholder}
              rows={textAreaRows}
              disabled={disabled}
              className={`text-base rounded-lg ${error ? "border-red-500" : ""} ${className || ""}`}
              style={{
                borderColor: error ? "#ef4444" : undefined,
                ...(inputStyle || {}),
              }}
              maxLength={maxLength}
              {...props}
            />
          ) : isAddressInput ? (
            <div className="">
              <Space.Compact className="w-[100%]">
                <Input
                  size="large"
                  value={addressNumber}
                  onChange={(e) => onAddressNumberChange?.(e.target.value)}
                  placeholder="No."
                  className="border-[1px] h-12 text-base"
                  style={{ width: "20%" }}
                  disabled={disabled}
                />
                <Input
                  size="large"
                  value={addressText}
                  onChange={(e) => onAddressTextChange?.(e.target.value)}
                  placeholder="Address"
                  className="address-input pl-[12px] ml-16 border-0 h-12 text-base"
                  style={{ width: "" }}
                  disabled={disabled}
                />
              </Space.Compact>
            </div>
          ) : isPhoneNumberInput ? (
            <Space.Compact size="large" style={{ width: "100%" }}>
              <Select
                value={
                  selectedCountryKey ??
                  (selectedPhoneCode
                    ? (() => {
                        const c = allCountryCodes.find((x) => `+${x.phone}` === selectedPhoneCode)
                        return c ? createCountryKey(c.code, selectedPhoneCode) : selectedPhoneCode
                      })()
                    : undefined)
                }
                onChange={(_key: string, option: any) => {
                  if (onPhoneCodeSelect && option?.dialCode != null) {
                    onPhoneCodeSelect(option.dialCode, option.countryKey)
                  }
                }}
                disabled={disabled}
                showSearch
                optionFilterProp="searchLabel"
                labelRender={(item) => {
                  const countryKey = (item?.value ?? item) as string
                  if (!countryKey) return null
                  const parts = countryKey.split("-")
                  const countryCode = parts[0] ?? ""
                  const dialCode = parts.length > 1 ? parts.slice(1).join("-") : ""
                  const flagUrl = getFlagImageUrl(countryCode) || "/placeholder.svg"
                  return (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <img
                        src={flagUrl}
                        alt={countryCode}
                        style={{ width: "24px", height: "16px", objectFit: "cover", flexShrink: 0 }}
                      />
                      <span>{dialCode}</span>
                    </div>
                  )
                }}
                style={{ width: "140px", height: "48px" }}
                styles={{ popup: { root: { minWidth: "280px" } } }}
                virtual={false}
                listItemHeight={48}
                options={phoneCountries
                  .map((country) => {
                    const countryData = allCountryCodes.find((c) => c.code === country.code)
                    const dialCode = countryData ? `+${countryData.phone}` : ""
                    if (!dialCode) return null
                    const countryKey = createCountryKey(country.code, dialCode)
                    return {
                      key: countryKey,
                      value: countryKey,
                      dialCode,
                      countryCode: country.code,
                      countryKey,
                      countryName: country.name,
                      label: (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <img
                            src={getFlagImageUrl(country.code) || "/placeholder.svg"}
                            alt={country.name}
                            style={{ width: "24px", height: "16px", objectFit: "cover" }}
                          />
                          <span>{dialCode}</span>
                        </div>
                      ),
                      searchLabel: `${country.name} ${country.code} ${dialCode}`.toLowerCase(),
                    }
                  })
                  .filter(Boolean)}
                optionRender={(option) => {
                  const countryKey = (option?.value ?? (option as any)?.data?.value) as string
                  const parts = typeof countryKey === "string" ? countryKey.split("-") : []
                  const countryCode = parts[0] ?? ""
                  const dialCode = parts.length > 1 ? parts.slice(1).join("-") : ""
                  const country = phoneCountries.find((c) => c.code === countryCode)
                  const countryName = country?.name ?? (option as any)?.countryName ?? ""
                  const flagUrl = getFlagImageUrl(countryCode) || "/placeholder.svg"
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        minHeight: "40px",
                        color: "rgba(0, 0, 0, 0.88)",
                        padding: "4px 0",
                      }}
                    >
                      <img
                        src={flagUrl}
                        alt={countryName}
                        style={{ width: "28px", height: "20px", objectFit: "cover", flexShrink: 0 }}
                      />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontWeight: 500, color: "rgba(0, 0, 0, 0.88)" }}>{countryName}</div>
                        <div style={{ fontSize: "12px", color: "rgba(0, 0, 0, 0.45)" }}>{dialCode}</div>
                      </div>
                    </div>
                  )
                }}
              />
              <Input
                ref={ref}
                size="large"
                value={phoneNumberValue}
                onChange={(e) => onPhoneNumberChange?.(e.target.value)}
                placeholder={props.placeholder || "Enter your phone number"}
                disabled={disabled}
                className={`h-12 ${error ? "border-red-500" : ""}`}
                style={{ borderColor: error ? "#ef4444" : undefined, height: "48px" }}
                maxLength={maxLength || 10}
              />
            </Space.Compact>
          ) : isCountrySearch ? (
            <div className="relative">
              <Select
                ref={ref}
                suffixIcon={<img src="/assets/icons/input-suffix.svg" alt="suffix" />}
                size="large"
                value={selectedCountry}
                placeholder={props.placeholder || "select country"}
                className={`w-full ${error ? "border-red-500" : ""} ${className || ""}`}
                style={{ height: "48px" }}
                open={isCountryDropdownOpen}
                onOpenChange={setIsCountryDropdownOpen}
                disabled={disabled}
                popupRender={() => (
                  <div className="p-0">
                    <div className="p-3 border-b border-gray-200">
                      <Input
                        size="large"
                        value={searchTerm}
                        onChange={handleCountrySearch}
                        placeholder="Search countries..."
                        prefix={<SearchOutlined className="text-gray-400" />}
                        suffix={
                          searchTerm && (
                            <CloseOutlined
                              className="text-gray-400 cursor-pointer hover:text-gray-600"
                              onClick={clearCountrySearch}
                            />
                          )
                        }
                        className="border-0 shadow-none"
                        disabled={disabled}
                      />
                    </div>
                    <div className="p-2">
                      <div className="text-sm text-gray-600 mb-2 px-2">Results</div>
                      <div className="max-h-48 overflow-y-auto">
                        {filteredCountries.map((country) => (
                          <div
                            key={country.code}
                            onClick={() => !disabled && handleCountrySelect(country.name)}
                            className={`px-3 py-2 rounded flex items-center space-x-3 ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:bg-gray-50"
                              }`}
                          >
                            <img
                              src={country.flag || "/placeholder.svg"}
                              alt={`${country.name} flag`}
                              className="w-6 h-4 object-cover rounded-sm"
                            />
                            <span className="text-sm">{country.name}</span>
                          </div>
                        ))}
                        {filteredCountries.length === 0 && (
                          <div className="px-3 py-2 text-gray-500 text-sm">No countries found</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                styles={{
                  popup: {
                    root: {
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                      padding: 0,
                    },
                  },
                }}
              />
            </div>
          ) : isPhoneInput ? (
            <Input.Group compact>
              <Select
                size="large"
                value={selectedCountryCode}
                onChange={handleCountryCodeChange}
                style={{ width: "25%", height: "48px" }}
                className="h-12"
                disabled={disabled}
                styles={{
                  popup: {
                    root: {
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    },
                  },
                }}
              >
                {countryCodes.map((country) => (
                  <Select.Option key={country.code} value={country.code}>
                    <span className="flex items-center">
                      <span className="mr-2">{country.flag}</span>
                      {country.code}
                    </span>
                  </Select.Option>
                ))}
              </Select>
              <Input
                ref={ref}
                size="large"
                value={phoneValue}
                onChange={handlePhoneChange}
                onPaste={handlePaste}
                className={`h-12 text-base ${error ? "border-red-500" : ""} ${className || ""}`}
                style={{ width: "75%", height: "48px", borderColor: error ? "#ef4444" : undefined }}
                disabled={disabled}
                {...props}
              />
            </Input.Group>
          ) : isDatePicker ? (
            <DatePicker
              ref={ref}
              size="large"
              value={datePickerValue}
              onChange={handleDateChange}
              disabledDate={disabledDate}
              className={`w-full h-12 text-base rounded-lg ${error ? "border-red-500" : ""} ${className || ""}`}
              style={{ borderColor: error ? "#ef4444" : undefined }}
              disabled={disabled}
              {...props}
            />
          ) : isMultiSelect ? (
            <Select
              ref={ref}
              size="large"
              mode="multiple"
              value={multiSelectValue || []}
              onChange={handleMultiSelectChange}
              placeholder={props.placeholder}
              className={`w-full  ${error ? "border-red-500" : ""} ${className || ""}`}
              style={{
                minHeight: "48px",
                height: "auto",
              }}
              showSearch
              filterOption={(input, option) => {
                const label = String(option?.label ?? "").toLowerCase()
                const value = String(option?.value ?? "").toLowerCase()
                const searchInput = input.toLowerCase()
                return label.includes(searchInput) || value.includes(searchInput)
              }}
              disabled={disabled}
              styles={{
                popup: {
                  root: {
                    borderRadius: "8px",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                  },
                },
              }}
              {...props}
            >
              {multiSelectOptions?.map((option) => (
                <Select.Option key={option.code || option.value} value={option.value} label={option.label}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          ) : isSelect ? (
            dropdownAsFormItem ? (
              <Form.Item name={fieldName} rules={fieldRules}>
                <Select
                  style={{ height: "48px" }}
                  className={`pl-12 w-full ${error ? "border-red-500" : ""} ${className || ""}`}
                  size="large"
                  options={options}
                  onChange={onChange}
                  suffixIcon={<img src="/assets/icons/input-suffix.svg" alt="suffix" />}
                  filterOption={true}
                  loading={disabled}
                  disabled={nonEditable || disabled}
                  placeholder={props.placeholder}
                />
              </Form.Item>
            ) : (
              <Select
                ref={ref}
                size="large"
                value={selectValue}
                onChange={handleSelectChange}
                placeholder={props.placeholder}
                className={`w-full ${error ? "border-red-500" : ""} ${className || ""}`}
                style={{
                  height: "48px",
                }}
                disabled={disabled}
                styles={{
                  popup: {
                    root: {
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    },
                  },
                }}
                {...props}
              >
                {options?.map((option) => (
                  <Select.Option key={option.code || option.value} value={option.value}>
                    {option.label}
                  </Select.Option>
                ))}
              </Select>
            )
          ) : showPasswordToggle ? (
            <Input.Password
              ref={ref}
              size="large"
              value={value}
              onChange={handleChange}
              onBlur={handleBlur}
              onPaste={handlePaste}
              className={`h-12 text-base rounded-lg ${error ? "border-red-500" : ""} ${className || ""}`}
              iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              style={{
                borderColor: error ? "#ef4444" : undefined,
              }}
              disabled={disabled}
              {...props}
            />
          ) : (
            <Input
              ref={ref}
              size="large"
              type={type}
              value={value}
              onChange={handleChange}
              onBlur={handleBlur}
              onPaste={handlePaste}
              className={`h-12 text-base rounded-lg ${error ? "border-red-500" : ""} ${className || ""}`}
              style={{
                borderColor: error ? "#ef4444" : undefined,
                ...(inputStyle || {}),
              }}
              disabled={disabled}
              required={required || false}
              maxLength={maxLength}
              max={type === "number" ? max : undefined}
              min={type === "number" ? min : undefined}
              {...props}
            />
          )}
          {nonEditable && (
            <div className="text-xs flex items-start p-1 mt-1">
              <AiOutlineInfoCircle className="text-lg mr-1.5" />
              {nonEditableText || "You cannot edit this"}
            </div>
          )}
        </div>
        {error && (
          <div className="text-red-500 text-sm mt-1 flex items-center">
            <span className="mr-1">⚠️</span>
            {error}
          </div>
        )}
      </div>
    )
  },
)

CustomInput.displayName = "CustomInput"

export default CustomInput
